import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Alert,
  Paper,
  Container
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { validateCesiumTokenAccess, getCesiumAsset } from '../utils/cesiumTokenUtils';
import { decodePair, encodeTransform, TRANSFORM_DEFAULT } from './utils/transformationCodec';
import BaseCesiumViewer from './BaseCesiumViewer';
import { ProfileProvider } from './context/ProfileContext';
import { CesiumErrorBoundary } from './components/CesiumErrorBoundary';

function PublicCesiumViewer() {
  const { t } = useTranslation();
  const params = useParams();
  const { assetId, gaussianAssetId, productsIds, transformationsParam, zTranslationParam, token } = params;

  // Decode transformation strings from the /t/:transformationsParam path segment
  // Fall back to legacy /z/:zTranslationParam (old share links) by encoding tz as a point transform
  const { transformationPointsStr, transformationGsStr } = (() => {
    if (transformationsParam != null) {
      const { point, gs } = decodePair(transformationsParam);
      return {
        transformationPointsStr: encodeTransform(point),
        transformationGsStr:     encodeTransform(gs),
      };
    }
    if (zTranslationParam != null) {
      const tz = parseFloat(zTranslationParam);
      return {
        transformationPointsStr: Number.isFinite(tz)
          ? encodeTransform({ ...TRANSFORM_DEFAULT, tz })
          : null,
        transformationGsStr: null,
      };
    }
    return { transformationPointsStr: null, transformationGsStr: null };
  })();

  const [error, setError] = useState(null);
  const [validatedToken, setValidatedToken] = useState(null);
  const [validatedAssetId, setValidatedAssetId] = useState(null);
  const [validatedGaussianAssetId, setValidatedGaussianAssetId] = useState(null);
  const [validatedProductsIds, setValidatedProductsIds] = useState(null);

  useEffect(() => {
    const loadSharedData = async () => {
      try {
        if (!assetId || !token) {
          throw new Error('Missing asset ID or token');
        }

        // Validate token access
        const hasAccess = await validateCesiumTokenAccess(token, assetId);
        if (!hasAccess) {
          throw new Error('Invalid or expired token');
        }

        // Get asset information
        await getCesiumAsset(assetId, token);

        // Validate gaussian splatting asset if provided
        if (gaussianAssetId) {
          await getCesiumAsset(gaussianAssetId, token);
        }

        // Parse and validate products IDs if provided
        let parsedProductsIds = null;
        if (productsIds) {
          try {
            parsedProductsIds = JSON.parse(decodeURIComponent(productsIds));
            if (Array.isArray(parsedProductsIds)) {
              for (const productId of parsedProductsIds) {
                if (productId) {
                  await getCesiumAsset(productId, token);
                }
              }
            }
          } catch (parseError) {
            console.error('Failed to parse products IDs:', parseError);
          }
        }

        // Set all validated values at once — BaseCesiumViewer is already
        // mounted and showing ViewerLoadingScreen while we validated.
        setValidatedToken(token);
        setValidatedAssetId(parseInt(assetId, 10));
        if (gaussianAssetId) {
          setValidatedGaussianAssetId(parseInt(gaussianAssetId, 10));
        }
        if (parsedProductsIds) {
          setValidatedProductsIds(parsedProductsIds);
        }

      } catch (err) {
        console.error('Failed to load shared data:', err);
        setError(err.message);
      }
    };

    loadSharedData();
  }, [assetId, gaussianAssetId, productsIds, token]);

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6">{t('UnableToLoadSharedView')}</Typography>
          <Typography>{error}</Typography>
        </Alert>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            {t('SharedLinkExpiredMessage')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('SharedLinkSecurityNote')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Render BaseCesiumViewer immediately. While token/assetId are null
  // (validation in progress), useCesiumViewer skips initialization and
  // ViewerLoadingScreen stays visible — giving a single branded loading
  // experience from start to tiles-loaded.
  return (
    <CesiumErrorBoundary>
      <ProfileProvider>
        <BaseCesiumViewer
          id={validatedAssetId}
          token={validatedToken}
          gaussianSplattingId={validatedGaussianAssetId}
          resultsAssetId={validatedProductsIds}
          transformationPointsStr={transformationPointsStr}
          transformationGsStr={transformationGsStr}
          isPublic={true}
        />
      </ProfileProvider>
    </CesiumErrorBoundary>
  );
}

export default PublicCesiumViewer;
